import { Add, FileUpload, Insights } from '@mui/icons-material';
import { Box, Button, Divider, Typography } from '@mui/material';
import { useCallback, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { enqueueSnackbar } from 'notistack';
import { useSetRecoilState } from 'recoil';
import { fetchDownloadDicomSeries } from '~/backend/connector-api/connector-components';
import AvailableImageItem from '~/components/ImageItem/AvailableImageItem';
import {
    domainEventBus,
    RegistrationCreateRequestedEvent,
} from '~/domain/events';
import { Image3D } from '~/domain/patient/image3d';
import { AvailableImage } from '~/hooks/image/usePrimaryImage';
import {
    useActivateRegistration,
    useRegistrationState,
} from '~/hooks/registration/useRegistrationStore';
import useBusyIndicator from '~/hooks/useBusyIndicator';
import useProcessDicomFiles from '~/hooks/useProcessDicomFiles';
import { imageListVersionState } from '~/state/atoms/image/imageAtoms';
import { workflowModalState, WorkflowTypes } from '~/state/atoms/viewerAtoms';
import RegistrationItem from '~/components/RegistrationItem/RegistrationItem';
import SelectableListItem from '~/components/SelectableListItem/SelectableListItem';

type Props = {
    primaryImage: Image3D;
    availableImages: AvailableImage[];
    selectedImageUid?: string;
    onSelectImage?: (image: AvailableImage) => void;
    showGenerateImage?: boolean;
};

export default function ImageList({
    primaryImage,
    availableImages,
    selectedImageUid,
    onSelectImage,
    showGenerateImage = false,
}: Props) {
    const intl = useIntl();
    const setWorkflowModal = useSetRecoilState(workflowModalState);
    const { processImportedFiles } = useProcessDicomFiles();
    const activateRegistration = useActivateRegistration();
    const registrationState = useRegistrationState();
    const { showBusy, hideBusy } = useBusyIndicator();
    const setImageListVersion = useSetRecoilState(imageListVersionState);

    const handleDownloadImage = useCallback(
        async (image: AvailableImage) => {
            await showBusy('Downloading image...');
            try {
                const dicomBlob = await fetchDownloadDicomSeries({
                    pathParams: {
                        studyId: image.studyInstanceUID,
                        seriesId: image.seriesInstanceUID,
                    },
                    queryParams: { series: true },
                });
                const files = [
                    new File([dicomBlob], `${image.seriesInstanceUID}.zip`),
                ];
                const result = await processImportedFiles(files);
                setImageListVersion((v) => v + 1);

                const loadedImage = result?.images?.find(
                    (img) => img.seriesUid === image.seriesInstanceUID
                );
                if (loadedImage) {
                    onSelectImage?.({
                        ...image,
                        isLoaded: true,
                        imageObject: loadedImage,
                    });
                }
            } catch (error) {
                enqueueSnackbar(
                    intl.formatMessage({ id: 'image.downloadFailed' }),
                    { variant: 'error' }
                );
            } finally {
                hideBusy();
            }
        },
        [
            intl,
            showBusy,
            hideBusy,
            processImportedFiles,
            setImageListVersion,
            onSelectImage,
        ]
    );

    const secondaryImages = useMemo(
        () =>
            availableImages.filter(
                (img) => img.seriesInstanceUID !== primaryImage.seriesUid
            ),
        [availableImages, primaryImage.seriesUid]
    );

    const handleOpenAiModel = () => {
        setWorkflowModal({
            open: true,
            page: WorkflowTypes.IMAGE,
        });
    };

    const handleFileChange = async (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        const files = event.target.files;
        if (!files?.length) {
            event.target.value = '';
            return;
        }

        const fileArray = Array.from(files);
        event.target.value = '';

        const result = await processImportedFiles(fileArray);
        const newImage = result?.images?.[0];

        if (!newImage || newImage.seriesUid === primaryImage.seriesUid) {
            return;
        }

        domainEventBus.publish(
            new RegistrationCreateRequestedEvent(
                primaryImage.seriesUid,
                newImage.seriesUid
            )
        );
    };

    const handleCreateRegistration = useCallback(
        (image: AvailableImage) => {
            if (!image.imageObject) {
                return;
            }
            domainEventBus.publish(
                new RegistrationCreateRequestedEvent(
                    primaryImage.seriesUid,
                    image.imageObject.seriesUid
                )
            );
        },
        [primaryImage.seriesUid]
    );

    const getRegistrationsForImage = (image: AvailableImage) => {
        const imageReg = registrationState.imageRegistrations.find(
            (r) => r.imageSeriesUid === image.seriesInstanceUID
        );
        if (!imageReg) {
            return [];
        }
        // Only show registrations where the secondary image
        // is the target (primary -> secondary direction)
        const secondaryForUid = image.imageObject?.frameOfReferenceUid;
        if (!secondaryForUid) {
            return imageReg.registrations;
        }
        return imageReg.registrations.filter(
            (reg) => reg.targetFrameOfReferenceUid === secondaryForUid
        );
    };

    return (
        <Box sx={{ overflow: 'hidden' }}>
            <Box px={1.5} pb={1}>
                <Typography fontSize={12} color="primary" fontWeight={500}>
                    <FormattedMessage id="workflow.imageRegistration.secondaryImages" />
                </Typography>
            </Box>
            {secondaryImages.length === 0 ? (
                <Box px={1.5} pb={0.5}>
                    <Typography fontSize={12} color="text.secondary" mb={1.5}>
                        <FormattedMessage
                            id={
                                showGenerateImage
                                    ? 'workflow.imageRegistration.noSecondaryImages'
                                    : 'workflow.imageRegistration.noSecondaryImagesRegistration'
                            }
                        />
                    </Typography>
                    <Box display="flex" flexDirection="column" gap={0.5}>
                        {showGenerateImage && (
                            <Button
                                fullWidth
                                variant="contained"
                                onClick={handleOpenAiModel}
                                sx={{
                                    textTransform: 'capitalize',
                                    pl: 2,
                                    fontSize: 12,
                                }}
                            >
                                <Insights
                                    sx={{
                                        position: 'absolute',
                                        left: 8,
                                        marginRight: 1,
                                        fontSize: 16,
                                    }}
                                />
                                <FormattedMessage id="generateSyntheticImage" />
                            </Button>
                        )}
                        <Button
                            fullWidth
                            variant="outlined"
                            component="label"
                            sx={{
                                textTransform: 'capitalize',
                                pl: 2,
                                fontSize: 12,
                            }}
                        >
                            <FileUpload
                                sx={{
                                    position: 'absolute',
                                    left: 8,
                                    marginRight: 1,
                                    fontSize: 16,
                                }}
                            />
                            <FormattedMessage id="loadImage" />
                            <input
                                type="file"
                                hidden
                                multiple
                                accept=".dcm,.dicom,.dc3,application/dicom,.gz,.dcm.gz"
                                onChange={handleFileChange}
                            />
                        </Button>
                    </Box>
                </Box>
            ) : (
                <Box>
                    {secondaryImages.map((image, index) => {
                        const isImageSelected =
                            selectedImageUid === image.seriesInstanceUID;
                        const regs = getRegistrationsForImage(image);
                        const sharesFOR = image.imageObject
                            ? image.imageObject.frameOfReferenceUid ===
                              primaryImage.frameOfReferenceUid
                            : false;
                        const canCreateRegistration =
                            image.imageObject && !sharesFOR;

                        return (
                            <Box key={image.seriesInstanceUID}>
                                {index > 0 && <Divider />}
                                <AvailableImageItem
                                    image={image}
                                    hasSharedFrameOfReference={sharesFOR}
                                    isSelected={isImageSelected}
                                    onDownload={
                                        !image.imageObject && !image.rawSeries
                                            ? () => handleDownloadImage(image)
                                            : undefined
                                    }
                                />
                                {regs.map((reg) => (
                                    <RegistrationItem
                                        key={reg.sopInstanceUid}
                                        label={reg.contentLabel}
                                        approvalStatus={reg.approvalStatus}
                                        isSelected={
                                            registrationState.selectedSopInstanceUid ===
                                                reg.sopInstanceUid &&
                                            registrationState.selectedImageSeriesUid ===
                                                image.seriesInstanceUID
                                        }
                                        onClick={() =>
                                            activateRegistration(
                                                reg.sopInstanceUid,
                                                image.seriesInstanceUID
                                            )
                                        }
                                    />
                                ))}
                                {canCreateRegistration && (
                                    <SelectableListItem
                                        data-testid="create-registration-button"
                                        onClick={() =>
                                            handleCreateRegistration(image)
                                        }
                                    >
                                        <Box
                                            display="flex"
                                            alignItems="center"
                                            gap={1}
                                        >
                                            <Add
                                                sx={{
                                                    fontSize: 18,
                                                    color: 'primary.main',
                                                }}
                                            />
                                            <Typography
                                                fontSize={12}
                                                color="primary"
                                            >
                                                <FormattedMessage id="workflow.imageRegistration.newRegistration" />
                                            </Typography>
                                        </Box>
                                    </SelectableListItem>
                                )}
                            </Box>
                        );
                    })}
                </Box>
            )}
        </Box>
    );
}
